import { LitElement, html, css } from 'lit'
import { api } from '../api.js'
import { toast } from './app-toast.js'

// Admin-only audit-activity reports over the last 30 days. Two views:
//   • By user   — time manually entered in Workamajig vs. billable / unbillable
//                 time Ziggy found (draft + approved) vs. time rejected.
//   • By source — the same Ziggy metrics (billable / unbillable found, rejected)
//                 broken down by data source (Calendar, Mail, Chat, …).
// Each row expands to its per-day breakdown; each view has a grand-total row.
class PageReports extends LitElement {
  static properties = {
    _view: { state: true },       // 'user' | 'source'
    _reports: { state: true },    // { user?: report, source?: report }
    _loading: { state: true },
    _denied: { state: true },
    _expanded: { state: true }    // Set of `${view}:${id}` keys
  }

  constructor() {
    super()
    this._view = 'user'
    this._reports = {}
    this._expanded = new Set()
  }

  static styles = css`
    h1 { color: var(--zg-brand); margin: 0 0 0.25rem; font-size: 1.4rem; }
    .lead { color: var(--zg-muted); margin: 0 0 1rem; font-size: 0.9rem; }
    .tabs { display: flex; gap: 0.5rem; margin: 0 0 1.25rem; }
    .tabs button { font: inherit; cursor: pointer; border: 1px solid var(--zg-border);
      background: var(--zg-surface); color: var(--zg-muted); border-radius: 999px;
      padding: 0.35rem 0.9rem; font-size: 0.85rem; }
    .tabs button.active { background: var(--zg-accent); color: var(--zg-accent-ink);
      border-color: var(--zg-accent); font-weight: 700; }
    .wrap { overflow-x: auto; }
    table { border-collapse: collapse; width: 100%; font-size: 0.9rem; min-width: 560px; }
    th, td { text-align: right; padding: 0.5rem 0.75rem; border-bottom: 1px solid var(--zg-border); }
    th:first-child, td:first-child { text-align: left; }
    thead th { color: var(--zg-muted); font-size: 0.75rem; text-transform: uppercase;
               letter-spacing: 0.3px; border-bottom: 2px solid var(--zg-border); }
    tr.group { cursor: pointer; }
    tr.group td { background: var(--zg-bg); font-weight: 700; color: var(--zg-brand); border-bottom: 2px solid var(--zg-border); }
    tr.group:hover td { background: var(--zg-border); }
    tr.group .caret { display: inline-block; width: 1em; color: var(--zg-muted); font-weight: 400; }
    tr.run td:first-child { padding-left: 1.9rem; }
    tr.grand td { font-weight: 800; color: var(--zg-brand); border-top: 2px solid var(--zg-brand);
                  border-bottom: none; font-size: 0.95rem; }
    td.num { font-variant-numeric: tabular-nums; }
    .muted { color: var(--zg-muted); }
    .empty { padding: 1.5rem 0; color: var(--zg-muted); }
  `

  connectedCallback() {
    super.connectedCallback()
    this._load('user')
  }

  // Lazy-load each view's report on first visit.
  async _load(view) {
    if (this._reports[view]) return
    this._loading = true
    const path = view === 'user' ? '/reports/audit-activity' : '/reports/audit-activity-by-source'
    try {
      const data = await api.get(path)
      this._reports = { ...this._reports, [view]: data }
    } catch (err) {
      if (err.code === 'FORBIDDEN' || err.statusCode === 403) this._denied = true
      else toast(err.message, 'error')
    } finally {
      this._loading = false
    }
  }

  _switch(view) {
    this._view = view
    this._load(view)
  }

  _toggle(key) {
    const next = new Set(this._expanded)
    next.has(key) ? next.delete(key) : next.add(key)
    this._expanded = next
  }

  // De-clutter zeros in per-run cells; totals render their own value.
  _cell(v) {
    return v > 0 ? html`${v}h` : html`<span class="muted">—</span>`
  }

  _table({ head, groups, groupKey, groupLabel, cols, total }) {
    const rows = []
    for (const g of groups) {
      const key = `${this._view}:${groupKey(g)}`
      const open = this._expanded.has(key)
      rows.push(html`
        <tr class="group" @click=${() => this._toggle(key)}
            title=${open ? 'Collapse' : 'Expand daily breakdown'}>
          <td><span class="caret">${open ? '▾' : '▸'}</span>${groupLabel(g)}</td>
          ${cols.map(c => html`<td class="num">${g.totals[c]}h</td>`)}
        </tr>`)
      if (open) {
        for (const run of g.runs) {
          rows.push(html`
            <tr class="run">
              <td>${run.date}</td>
              ${cols.map(c => html`<td class="num">${this._cell(run[c])}</td>`)}
            </tr>`)
        }
      }
    }
    return html`
      <table>
        <thead><tr>${head.map(h => html`<th>${h}</th>`)}</tr></thead>
        <tbody>
          ${rows}
          <tr class="grand">
            <td>Grand total</td>
            ${cols.map(c => html`<td class="num">${total[c]}h</td>`)}
          </tr>
        </tbody>
      </table>`
  }

  render() {
    if (this._denied) return html`<p class="empty">You don't have access to this report.</p>`
    const isUser = this._view === 'user'
    const r = this._reports[this._view]
    const groups = r ? (isUser ? r.users : r.sources) : null

    return html`
      <h1>Audit activity</h1>
      <p class="lead">
        ${isUser
          ? 'Time manually entered in Workamajig vs. what Ziggy found (billable / unbillable), what was rejected, and the under-logged time Ziggy flagged'
          : 'What Ziggy found by data source — billable / unbillable / rejected, from each source’s own estimates'}${r
          ? html` over the last ${r.days} days (${r.from} → ${r.to}).` : '.'}
        Click a row for its daily breakdown.
      </p>

      <div class="tabs">
        <button class=${isUser ? 'active' : ''} @click=${() => this._switch('user')}>By user</button>
        <button class=${!isUser ? 'active' : ''} @click=${() => this._switch('source')}>By source</button>
      </div>

      ${!r
        ? html`<p class="empty">Loading…</p>`
        : groups.length === 0
          ? html`<p class="empty">No activity in this window.</p>`
          : html`<div class="wrap">${isUser
              ? this._table({
                  head: ['User', 'Manual WMJ', 'Billable found', 'Unbillable found', 'Rejected', 'Underreported'],
                  groups,
                  groupKey: u => u.userId,
                  groupLabel: u => html`${u.name} <span class="muted">· ${u.email}</span>`,
                  cols: ['manualWmj', 'billableFound', 'unbillableFound', 'rejected', 'underreported'],
                  total: r.grandTotal
                })
              : this._table({
                  head: ['Source', 'Billable found', 'Unbillable found', 'Rejected'],
                  groups,
                  groupKey: s => s.source,
                  groupLabel: s => s.label,
                  cols: ['billableFound', 'unbillableFound', 'rejected'],
                  total: r.grandTotal
                })}</div>`}
    `
  }
}

customElements.define('page-reports', PageReports)
